import React, { FC, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import { PaletteMode, Theme, ThemeProvider } from '@mui/material';

import { setCssVariables } from '~/helpers/cssVariables';
import type { GetPaletteOptions } from '~/projects/justdone/theme/palette';

import { useIsHomePageRedesignEnabled } from './useIsHomePageRedesignEnabled';

export const HOME_PAGE_REDESIGN_THEME_VARIANT = 'homePageRedesign';
export const LEGACY_THEME_VARIANT = 'legacy';

export const PROJECT_THEME_CSS_VARIABLE_GROUPS = [
  'common',
  'neutral',
  'primary',
  'secondary',
  'error',
  'warning',
  'surface',
  'info',
  'success',
  'custom',
];

export type CreateCustomTheme = (
  mode: PaletteMode,
  options?: GetPaletteOptions,
) => Theme;

type Props = {
  children: ReactNode;
  createCustomTheme: CreateCustomTheme;
  mode?: PaletteMode;
};

export const HomePageRedesignThemeProvider: FC<Props> = ({
  children,
  createCustomTheme,
  mode = 'light',
}) => {
  const isHomePageRedesignEnabled = useIsHomePageRedesignEnabled();
  const variant = isHomePageRedesignEnabled
    ? HOME_PAGE_REDESIGN_THEME_VARIANT
    : LEGACY_THEME_VARIANT;
  const theme = useMemo(
    () => createCustomTheme(mode, { variant }),
    [createCustomTheme, mode, variant],
  );

  useEffect(() => {
    setCssVariables(theme.palette, PROJECT_THEME_CSS_VARIABLE_GROUPS);
  }, [theme]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
